/*
 OK FBI — Investigator Sound Pack (Theme-aware)
 Two cinematic palettes:
   • noir             → warm, analog, procedural (sawtooth/triangle, lowpass filters)
   • modern_forensic  → crisp, digital, high-tech (square/sine bursts, bandpass beeps)
 Both are procedurally generated via Web Audio (zero external files → OBS-friendly).
 Rate-limited via internal cooldown.
*/

let ctx = null, master = null;
let enabled = true, volume = 0.15;
let theme = "noir";
let lastFireAt = 0;

const STORE = "okfbi_soundpack";
try {
  const s = JSON.parse(localStorage.getItem(STORE) || "{}");
  if (typeof s.enabled === "boolean") enabled = s.enabled;
  if (typeof s.volume === "number") volume = Math.max(0, Math.min(1, s.volume));
  if (typeof s.theme === "string" && (s.theme === "noir" || s.theme === "modern")) theme = s.theme;
} catch {}

function persist() { try { localStorage.setItem(STORE, JSON.stringify({ enabled, volume, theme })); } catch {} }

export function setEnabled(v) { enabled = !!v; persist(); }
export function isEnabled() { return enabled; }
export function setVolume(v) { volume = Math.max(0, Math.min(1, Number(v) || 0)); persist(); }
export function getVolume() { return volume; }
export function setTheme(t) { if (t === "noir" || t === "modern") { theme = t; persist(); } }
export function getTheme() { return theme; }

function ensureCtx() {
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      master = ctx.createGain();
      master.gain.value = 1.0;
      master.connect(ctx.destination);
    } catch {}
  }
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// Core primitives
function tone({ freq = 440, dur = 0.15, type = "sine", vol = 0.5, attack = 0.005, release = 0.08, freqEnd = null, filterFreq = null, filterType = "lowpass", delay = 0 }) {
  const c = ensureCtx(); if (!c) return;
  const now = c.currentTime + delay;
  const osc = c.createOscillator(); const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  if (freqEnd) osc.frequency.exponentialRampToValueAtTime(freqEnd, now + dur);
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(vol * volume, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur + release);
  let dest = g;
  if (filterFreq) {
    const f = c.createBiquadFilter(); f.type = filterType; f.frequency.value = filterFreq; f.Q.value = 0.7;
    g.connect(f); dest = f;
  }
  osc.connect(g); dest.connect(master);
  osc.start(now); osc.stop(now + dur + release + 0.05);
}
function noise({ dur = 0.15, vol = 0.15, filter = 1200, filterType = "bandpass", Q = 1, delay = 0 }) {
  const c = ensureCtx(); if (!c) return;
  const now = c.currentTime + delay;
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1);
  const src = c.createBufferSource(); src.buffer = buf;
  const f = c.createBiquadFilter(); f.type = filterType; f.frequency.value = filter; f.Q.value = Q;
  const g = c.createGain();
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(vol * volume, now + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  src.connect(f); f.connect(g); g.connect(master);
  src.start(now); src.stop(now + dur + 0.05);
}

// ============ THEME: NOIR — warm, analog, cinematic (peaks ≤0.4) ============
const NOIR = {
  evidence_reveal: () => { tone({ freq: 660, dur: 0.08, vol: 0.28 }); tone({ freq: 990, dur: 0.16, vol: 0.24, delay: 0.09 }); },
  insight:         () => { tone({ freq: 520, dur: 0.06, vol: 0.22 }); tone({ freq: 780, dur: 0.12, vol: 0.2, delay: 0.06 }); tone({ freq: 1050, dur: 0.14, vol: 0.16, delay: 0.16 }); },
  file_open:       () => { tone({ freq: 220, freqEnd: 130, dur: 0.2, type: "triangle", vol: 0.28, filterFreq: 900 }); noise({ dur: 0.12, vol: 0.09, filter: 500, filterType: "lowpass", delay: 0.06 }); },
  play_recording:  () => { tone({ freq: 90, freqEnd: 480, dur: 0.32, type: "sawtooth", vol: 0.18, filterFreq: 900 }); tone({ freq: 1600, dur: 0.03, type: "square", vol: 0.12, delay: 0.32 }); },
  cctv_open:       () => { tone({ freq: 100, freqEnd: 520, dur: 0.28, type: "sawtooth", vol: 0.18, filterFreq: 1000 }); tone({ freq: 1200, dur: 0.04, type: "square", vol: 0.14, delay: 0.28 }); },
  fingerprint_result: () => { tone({ freq: 180, freqEnd: 320, dur: 0.28, type: "sawtooth", vol: 0.16 }); tone({ freq: 880, dur: 0.05, type: "square", vol: 0.14, delay: 0.28 }); },
  dna_result:      () => { for (let i = 0; i < 3; i++) tone({ freq: 520 + i * 80, dur: 0.08, vol: 0.16, delay: i * 0.08 }); tone({ freq: 990, dur: 0.14, vol: 0.15, delay: 0.28 }); },
  voting_start:    () => { tone({ freq: 165, dur: 0.25, type: "sine", vol: 0.28, filterFreq: 700 }); tone({ freq: 330, dur: 0.14, vol: 0.16, delay: 0.15 }); },
  voting_end:      () => { tone({ freq: 130, dur: 0.35, type: "sine", vol: 0.3, filterFreq: 500 }); noise({ dur: 0.08, vol: 0.09, filter: 300, filterType: "lowpass", delay: 0.05 }); },
  answer_correct:  () => { tone({ freq: 660, dur: 0.08, vol: 0.22 }); tone({ freq: 990, dur: 0.14, vol: 0.2, delay: 0.08 }); tone({ freq: 1320, dur: 0.18, vol: 0.16, delay: 0.20 }); },
  answer_wrong:    () => { tone({ freq: 220, freqEnd: 110, dur: 0.35, type: "sawtooth", vol: 0.22, filterFreq: 500 }); },
  suspicion:       () => { tone({ freq: 55, dur: 0.5, type: "sine", vol: 0.28 }); tone({ freq: 82, dur: 0.4, type: "triangle", vol: 0.14, delay: 0.05 }); },
  contradiction:   () => { tone({ freq: 90, freqEnd: 70, dur: 0.6, type: "sawtooth", vol: 0.22, filterFreq: 400 }); tone({ freq: 660, dur: 0.03, type: "square", vol: 0.14, delay: 0.6 }); },
  accuse:          () => { tone({ freq: 220, freqEnd: 165, dur: 0.35, type: "sawtooth", vol: 0.2, filterFreq: 700 }); tone({ freq: 110, dur: 0.5, type: "sine", vol: 0.28, delay: 0.30 }); },
  case_solved:     () => { tone({ freq: 82, dur: 0.9, vol: 0.36 }); tone({ freq: 165, dur: 0.9, type: "triangle", vol: 0.2, filterFreq: 500 }); tone({ freq: 660, dur: 0.5, vol: 0.16, delay: 0.35 }); tone({ freq: 880, dur: 0.6, vol: 0.14, delay: 0.5 }); },
  case_failed:     () => { tone({ freq: 220, freqEnd: 90, dur: 1.2, type: "sawtooth", vol: 0.22, filterFreq: 380 }); tone({ freq: 82, dur: 0.9, vol: 0.28, delay: 0.2 }); },
};

// ============ THEME: MODERN FORENSIC — crisp, digital, high-tech ============
// Sharper attacks, sine/square lead tones, filtered bandpass beeps, no lowpass smear.
const MODERN = {
  evidence_reveal: () => { tone({ freq: 1400, dur: 0.05, type: "sine", vol: 0.24 }); tone({ freq: 2100, dur: 0.09, type: "sine", vol: 0.22, delay: 0.06 }); noise({ dur: 0.04, vol: 0.05, filter: 4500, filterType: "highpass", delay: 0.02 }); },
  insight:         () => { tone({ freq: 1600, dur: 0.05, type: "square", vol: 0.18 }); tone({ freq: 2400, dur: 0.08, type: "square", vol: 0.16, delay: 0.05 }); tone({ freq: 3200, dur: 0.10, type: "sine", vol: 0.14, delay: 0.12 }); },
  file_open:       () => { tone({ freq: 900, freqEnd: 1600, dur: 0.10, type: "sine", vol: 0.22 }); tone({ freq: 2200, dur: 0.03, type: "square", vol: 0.14, delay: 0.10 }); noise({ dur: 0.05, vol: 0.05, filter: 6000, filterType: "highpass", delay: 0.03 }); },
  play_recording:  () => { tone({ freq: 2400, dur: 0.03, type: "square", vol: 0.15 }); tone({ freq: 2400, dur: 0.03, type: "square", vol: 0.15, delay: 0.06 }); tone({ freq: 1800, freqEnd: 3200, dur: 0.12, type: "sine", vol: 0.16, delay: 0.14 }); },
  cctv_open:       () => { tone({ freq: 1200, freqEnd: 2400, dur: 0.10, type: "sine", vol: 0.18 }); tone({ freq: 3600, dur: 0.03, type: "square", vol: 0.15, delay: 0.10 }); noise({ dur: 0.06, vol: 0.05, filter: 8000, filterType: "highpass", delay: 0.04 }); },
  fingerprint_result: () => { for (let i = 0; i < 4; i++) tone({ freq: 1800 + i * 120, dur: 0.04, type: "sine", vol: 0.14, delay: i * 0.05 }); tone({ freq: 2600, dur: 0.10, type: "square", vol: 0.16, delay: 0.22 }); },
  dna_result:      () => { for (let i = 0; i < 5; i++) tone({ freq: 1600 + i * 200, dur: 0.05, type: "sine", vol: 0.14, delay: i * 0.05 }); tone({ freq: 2800, dur: 0.14, type: "square", vol: 0.16, delay: 0.28 }); },
  voting_start:    () => { tone({ freq: 800, dur: 0.06, type: "sine", vol: 0.20 }); tone({ freq: 1200, dur: 0.06, type: "sine", vol: 0.20, delay: 0.08 }); tone({ freq: 1600, dur: 0.12, type: "sine", vol: 0.18, delay: 0.16 }); },
  voting_end:      () => { tone({ freq: 1600, dur: 0.06, type: "sine", vol: 0.20 }); tone({ freq: 1200, dur: 0.06, type: "sine", vol: 0.18, delay: 0.08 }); tone({ freq: 800, dur: 0.14, type: "sine", vol: 0.18, delay: 0.16 }); },
  answer_correct:  () => { tone({ freq: 1320, dur: 0.06, type: "sine", vol: 0.22 }); tone({ freq: 1760, dur: 0.06, type: "sine", vol: 0.20, delay: 0.07 }); tone({ freq: 2640, dur: 0.14, type: "sine", vol: 0.18, delay: 0.14 }); },
  answer_wrong:    () => { tone({ freq: 440, dur: 0.08, type: "square", vol: 0.22 }); tone({ freq: 330, dur: 0.20, type: "square", vol: 0.22, delay: 0.09 }); },
  suspicion:       () => { tone({ freq: 220, dur: 0.4, type: "sine", vol: 0.24, filterFreq: 3200, filterType: "highpass" }); tone({ freq: 330, dur: 0.35, type: "sine", vol: 0.14, delay: 0.05 }); },
  contradiction:   () => { tone({ freq: 3200, dur: 0.03, type: "square", vol: 0.18 }); tone({ freq: 2200, dur: 0.03, type: "square", vol: 0.16, delay: 0.05 }); tone({ freq: 3200, dur: 0.05, type: "square", vol: 0.18, delay: 0.10 }); },
  accuse:          () => { tone({ freq: 880, dur: 0.05, type: "square", vol: 0.22 }); tone({ freq: 440, dur: 0.15, type: "square", vol: 0.22, delay: 0.06 }); tone({ freq: 220, dur: 0.30, type: "sine", vol: 0.20, delay: 0.20 }); },
  case_solved:     () => { for (let i = 0; i < 4; i++) tone({ freq: 880 + i * 220, dur: 0.10, type: "sine", vol: 0.20, delay: i * 0.08 }); tone({ freq: 2200, dur: 0.28, type: "sine", vol: 0.18, delay: 0.40 }); },
  case_failed:     () => { tone({ freq: 440, freqEnd: 110, dur: 0.9, type: "square", vol: 0.22 }); tone({ freq: 220, dur: 0.6, type: "sine", vol: 0.20, delay: 0.15 }); },
};

const THEMES = { noir: NOIR, modern: MODERN };
export const SFX_KEYS = Object.keys(NOIR);

/** Fire a sound. Rate-limited to prevent overlap spam. */
export function fire(key) {
  if (!enabled) return false;
  const pack = THEMES[theme] || NOIR;
  if (!pack[key]) return false;
  const now = performance.now();
  if (now - lastFireAt < 180) return false; // hard cooldown 180ms
  lastFireAt = now;
  try { pack[key](); return true; } catch { return false; }
}

// Unlock on user interaction (browser policy)
if (typeof window !== "undefined") {
  const unlock = () => { ensureCtx(); window.removeEventListener("click", unlock); window.removeEventListener("keydown", unlock); };
  window.addEventListener("click", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
}
